/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  HStack,
  Stack,
  FormControl,
  FormLabel,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  InputGroup,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import { Control, Controller, FieldErrors, UseFormSetValue } from 'react-hook-form';
import CustomInput from '../Input';
import ImageUpload from '../ImageUpload';
import { useCallback, useState, useEffect } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import LocationPin from '@/assets/svg/location.svg?react';
import CustomSelect from '../CustomSelect';
import { useGetCurrency } from '@/services/addProduct';

interface IRenderFieldsProps {
  page: number;
  control: Control<any>;
  errors: FieldErrors<{
    company_details: {
      name: string;
      address: string;
      company_certificate: Blob;
      pan_vat_certificate: string;
      currency_preference: 'NPR' | 'USD';
    };
    store_details: {
      name: string;
      warehouse_address: string;
      contact_number: string;
      store_logo: string;
    };
    bank_details: {
      bank_name: string;
      account_holder_name: string;
      branch_code: string;
      account_number: string;
      bank_cheque_book_scan_photo: string;
    };
  }>;
  setValue: UseFormSetValue<any>;
  isGoogleMapsLoaded: boolean;
  apiLoadError?: string;
}

const currencyOptions = [
  { id: 'USD', label: 'USD ($)' },
  { id: 'NPR', label: 'NPR (Rs.)' },
];

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

const defaultCenter = {
  lat: 27.7172, // Default to Kathmandu, Nepal
  lng: 85.324,
};

// Reusable component for required field label with red asterisk
const RequiredLabel = ({ label, isRequired = false }: { label: string; isRequired?: boolean }) => {
  return (
    <FormLabel fontSize="sm" color="text.secondary" display="inline-flex" alignItems="center">
      {label}
      {isRequired && (
        <Text as="span" color="brand.primary.DEFAULT" ml={1}>
          *
        </Text>
      )}
    </FormLabel>
  );
};

// Reusable component for address fields with Google Maps picker
export const AddressFieldWithMap = ({
  control,
  fieldName,
  label = '',
  error,
  isGoogleMapsLoaded,
  setValue, // Add setValue prop
}: {
  control: any;
  fieldName: string;
  label: string;
  error: any;
  isGoogleMapsLoaded: boolean;
  setValue: (name: string, value: any, options?: any) => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [address, setAddress] = useState<string>('');
  const [mapError, setMapError] = useState<string | null>(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);

  // Get current location when modal opens
  useEffect(() => {
    if (isOpen && isGoogleMapsLoaded) {
      setIsFetchingLocation(true);

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const newLocation = { lat: latitude, lng: longitude };
            setSelectedLocation(newLocation);
            setMapCenter(newLocation);
            setIsFetchingLocation(false);

            // Reverse geocode to get address
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: newLocation }, (results, status) => {
              if (status === 'OK' && results?.[0]) {
                setAddress(results[0].formatted_address);
              } else {
                setAddress('Address not found');
              }
            });
          },
          (error) => {
            console.error('Error getting location:', error);
            setMapError('Could not get your current location. Please select manually.');
            setSelectedLocation(defaultCenter);
            setMapCenter(defaultCenter);
            setIsFetchingLocation(false);
          },
        );
      } else {
        setMapError('Geolocation is not supported by this browser.');
        setSelectedLocation(defaultCenter);
        setMapCenter(defaultCenter);
        setIsFetchingLocation(false);
      }
    }
  }, [isOpen, isGoogleMapsLoaded]);

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const newLocation = { lat, lng };

      setSelectedLocation(newLocation);
      setMapCenter(newLocation);

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: newLocation }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          setAddress(results[0].formatted_address);
        } else {
          setAddress('Address not found');
        }
      });
    },
    [isGoogleMapsLoaded],
  );

  return (
    <FormControl isInvalid={!!error}>
      <RequiredLabel label={label} isRequired />
      <InputGroup>
        <Controller
          name={fieldName}
          control={control}
          rules={{ required: `${label} is required` }} // Add validation
          render={({ field }) => (
            <>
              <CustomInput
                {...field}
                value={address || field.value || ''}
                id={fieldName.replace('.', '_')}
                fontSize={'md'}
                placeholder={`Enter or select ${label.toLowerCase()}`}
                error={error}
                required
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e); // Update form state
                  setAddress(e.target.value); // Sync local state
                }}
              />
              <InputRightElement>
                <Button onClick={onOpen} variant="ghost" color="brand.primary.DEFAULT" size="sm">
                  <Icon as={LocationPin} boxSize="20px" />
                </Button>
              </InputRightElement>
            </>
          )}
        />
      </InputGroup>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Location</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isGoogleMapsLoaded ? (
              <>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={12}
                  onClick={handleMapClick}
                >
                  {selectedLocation && <Marker position={selectedLocation} />}
                </GoogleMap>

                {isFetchingLocation && (
                  <Text color="text.secondary" mt={2}>
                    Fetching your current location...
                  </Text>
                )}

                {mapError && (
                  <Text color="red.500" mt={2}>
                    {mapError}
                  </Text>
                )}

                {address && (
                  <Box mt={4}>
                    <strong>Selected Address:</strong> {address}
                  </Box>
                )}
              </>
            ) : (
              <Text color="red.500">
                Google Maps API failed to load. Please check your API key or network connection.
              </Text>
            )}
            <HStack mt={4} justify="flex-end">
              <Button onClick={onClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (address) {
                    setValue(fieldName, address, { shouldValidate: true }); // Directly update form state
                    onClose();
                  }
                }}
                bg="brand.primary.DEFAULT"
                color="white"
                fontWeight="semibold"
                fontSize="sm"
                fontFamily={'secondary'}
                _hover={{ bg: 'brand.primary.600' }}
                _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                isDisabled={!address || isFetchingLocation}
              >
                Confirm
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </FormControl>
  );
};

export const renderFields = ({
  control,
  page,
  errors,
  isGoogleMapsLoaded,
  apiLoadError,
  setValue,
}: IRenderFieldsProps) => {
  if (apiLoadError) {
    return <Text color="red.500">Google Maps failed to load: {apiLoadError}</Text>;
  }

  const SetInitialCurrency = () => {
    const { data: currencyData } = useGetCurrency();
    const [initialCurrencySet, setInitialCurrencySet] = useState(false);

    useEffect(() => {
      if (typeof setValue !== 'function') {
        console.error("renderFields Error: The 'setValue' prop is not a function.");
        return;
      }

      if (!initialCurrencySet && currencyData) {
        // Only set initial value if the field is empty
        const currentValue = control._formValues.company_details?.currency_preference;
        if (!currentValue) {
          setValue('company_details.currency_preference', currencyData?.currency || 'USD');
          setInitialCurrencySet(true);
        }
      }
    }, [currencyData, initialCurrencySet, setValue, control]);

    return null;
  };

  const stepKey = `step-${page}`;
  switch (page) {
    case 0:
      return (
        <Stack width={'100%'} direction={'column'} gap={4} key={stepKey}>
          <SetInitialCurrency />
          <Controller
            name="company_details.name"
            control={control}
            rules={{ required: 'Company name is required' }}
            render={({ field }) => (
              <FormControl isInvalid={!!errors.company_details?.name}>
                <RequiredLabel label="Company Name" isRequired />
                <CustomInput
                  {...field}
                  id="company_name"
                  fontSize={'md'}
                  placeholder="Enter the company name"
                  error={errors.company_details?.name}
                  required
                />
              </FormControl>
            )}
          />
          <AddressFieldWithMap
            control={control}
            fieldName="company_details.address"
            label="Company Address"
            error={errors.company_details?.address}
            isGoogleMapsLoaded={isGoogleMapsLoaded}
            setValue={setValue} // Pass setValue
          />
          <Controller
            name="company_details.currency_preference"
            control={control}
            rules={{ required: 'Currency preference is required' }}
            render={({ field }) => (
              <FormControl isInvalid={!!errors.company_details?.currency_preference}>
                <RequiredLabel label="Preferred Currency" isRequired />
                <CustomSelect
                  options={currencyOptions}
                  selectedValue={field.value}
                  onChange={(value: string) => {
                    field.onChange(value as 'NPR' | 'USD');
                  }}
                  size="md"
                  isSearchable={false}
                  isFullWidth
                />
                {errors.company_details?.currency_preference && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.company_details.currency_preference.message}
                  </Text>
                )}
              </FormControl>
            )}
          />
          <Controller
            name="company_details.company_certificate"
            control={control}
            rules={{ required: 'Company Certificate is required' }}
            render={({ field, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <RequiredLabel label="Company Certificate" isRequired />
                <ImageUpload
                  error={error}
                  onChange={(file) => field.onChange(file)}
                  // initialImage={field.value ? URL.createObjectURL(field.value) : null}
                  initialImage={
                    field.value instanceof Blob
                      ? URL.createObjectURL(field.value)
                      : typeof field.value === 'string'
                        ? field.value // assume it’s already a valid URL from API
                        : null
                  }
                  required
                />
              </FormControl>
            )}
          />
          <Controller
            name="company_details.pan_vat_certificate"
            control={control}
            rules={{ required: 'PAN/VAT Certificate is required.' }}
            render={({ field, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <RequiredLabel label="PAN/VAT Certificate" isRequired />
                <ImageUpload
                  error={error}
                  onChange={(file) => field.onChange(file)}
                  initialImage={
                    field.value instanceof Blob
                      ? URL.createObjectURL(field.value)
                      : typeof field.value === 'string'
                        ? field.value // assume it’s already a valid URL from API
                        : null
                  }
                  required
                />
              </FormControl>
            )}
          />
        </Stack>
      );
    case 1:
      return (
        <Stack width={'100%'} direction={'column'} gap={4} key={stepKey}>
          <Controller
            name="store_details.name"
            control={control}
            rules={{ required: 'Store name is required' }}
            render={({ field }) => (
              <FormControl isInvalid={!!errors.store_details?.name}>
                <RequiredLabel label="Name" isRequired />
                <CustomInput
                  {...field}
                  id="store_name"
                  fontSize={'md'}
                  placeholder="Enter the store name"
                  error={errors.store_details?.name}
                  required
                />
              </FormControl>
            )}
          />
          <AddressFieldWithMap
            control={control}
            fieldName="store_details.warehouse_address"
            label="Warehouse Address"
            error={errors.store_details?.warehouse_address}
            isGoogleMapsLoaded={isGoogleMapsLoaded}
            setValue={setValue} // Pass setValue
          />
          <Controller
            name="store_details.contact_number"
            control={control}
            rules={{ required: 'Contact Number is required' }}
            render={({ field }) => (
              <FormControl isInvalid={!!errors.store_details?.contact_number}>
                <RequiredLabel label="Contact Number" isRequired />
                <CustomInput
                  {...field}
                  id="contact_number"
                  fontSize={'md'}
                  placeholder="Enter the number (e.g., +9779812345678 or 9812345678)"
                  error={errors.store_details?.contact_number}
                  required
                  type="tel"
                  onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                    let value = e.target.value.replace(/[^0-9+]/g, ''); // Allow only numbers and +
                    if (value.includes('+') && !value.startsWith('+')) {
                      value = value.replace(/\+/g, ''); // Remove + if not at start
                    }
                    if (value.startsWith('+')) {
                      // Allow + and up to 13 digits
                      const digits = value.replace('+', '');
                      if (digits.length > 13) {
                        value = '+' + digits.slice(0, 13);
                      }
                    } else {
                      // Allow up to 10 digits
                      if (value.length > 10) {
                        value = value.slice(0, 10);
                      }
                    }
                    e.target.value = value;
                    field.onChange(value);
                  }}
                />
              </FormControl>
            )}
          />
          <Controller
            name="store_details.store_logo"
            control={control}
            rules={{ required: 'Store logo is required' }}
            render={({ field, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <RequiredLabel label="Store Logo" isRequired />
                <ImageUpload
                  error={error}
                  onChange={(file) => field.onChange(file)}
                  initialImage={
                    field.value instanceof Blob
                      ? URL.createObjectURL(field.value)
                      : typeof field.value === 'string'
                        ? field.value // assume it’s already a valid URL from API
                        : null
                  }
                  required
                />
              </FormControl>
            )}
          />
        </Stack>
      );
    case 2:
      return (
        <Stack width={'100%'} direction={'column'} gap={4} key={stepKey}>
          <Controller
            name="bank_details.bank_name"
            control={control}
            rules={{ required: 'Bank name is required' }}
            render={({ field }) => (
              <FormControl isInvalid={!!errors.bank_details?.bank_name}>
                <RequiredLabel label="Bank Name" isRequired />
                <CustomInput
                  {...field}
                  id="bank_name"
                  fontSize={'md'}
                  placeholder="Enter the bank name"
                  error={errors.bank_details?.bank_name}
                  required
                />
              </FormControl>
            )}
          />
          <Controller
            name="bank_details.account_holder_name"
            control={control}
            rules={{ required: 'Account holder name is required' }}
            render={({ field }) => (
              <FormControl isInvalid={!!errors.bank_details?.account_holder_name}>
                <RequiredLabel label="Account Holder Name" isRequired />
                <CustomInput
                  {...field}
                  id="account_holder_name"
                  fontSize={'md'}
                  placeholder="Enter account holder name"
                  error={errors.bank_details?.account_holder_name}
                  required
                />
              </FormControl>
            )}
          />
          <HStack spacing={4}>
            <Controller
              name="bank_details.branch_code"
              control={control}
              rules={{ required: 'Branch Name is required' }}
              render={({ field }) => (
                <FormControl isInvalid={!!errors.bank_details?.branch_code}>
                  <RequiredLabel label="Branch Name" isRequired />
                  <CustomInput
                    {...field}
                    id="branch_code"
                    fontSize={'md'}
                    placeholder="Enter branch name"
                    error={errors.bank_details?.branch_code}
                    required
                  />
                </FormControl>
              )}
            />
            <Controller
              name="bank_details.account_number"
              control={control}
              rules={{
                required: 'Account number is required',
                minLength: {
                  value: 4,
                  message: 'Account number must be at least 4 characters',
                },
                maxLength: {
                  value: 34,
                  message: 'Account number cannot exceed 34 characters',
                },
              }}
              render={({ field }) => (
                <FormControl isInvalid={!!errors.bank_details?.account_number}>
                  <RequiredLabel label="Account Number" isRequired />
                  <CustomInput
                    {...field}
                    id="account_number"
                    fontSize={'md'}
                    placeholder="Enter account number"
                    error={errors.bank_details?.account_number}
                    minLength={4}
                    maxLength={34}
                    required
                  />
                </FormControl>
              )}
            />
          </HStack>
          <Controller
            name="bank_details.bank_cheque_book_scan_photo"
            control={control}
            rules={{ required: 'Bank cheque book scan/photo is required' }}
            render={({ field, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <RequiredLabel label="Bank Cheque Book Scan/Photo" isRequired />
                <ImageUpload
                  error={error}
                  onChange={(file) => field.onChange(file)}
                  initialImage={
                    field.value instanceof Blob
                      ? URL.createObjectURL(field.value)
                      : typeof field.value === 'string'
                        ? field.value // assume it’s already a valid URL from API
                        : null
                  }
                  required
                />
              </FormControl>
            )}
          />
        </Stack>
      );
    default:
      return null;
  }
};
/* eslint-disable @typescript-eslint/no-explicit-any */
